import { useState } from 'react';
import { atlasProposalApprove, atlasProposalReject, type AtlasProposal, type AtlasProposalsResponse } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import type { QueryState } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { sx } from '../../ui/designStyles';
import { ErrorState, Skeleton } from '../../ui/states';
import { inWords, noDash, stop, useNow, type AtlasRole } from './common';

type Done = { state: 'approved' | 'rejected' } | { state: 'held'; note: string } | { state: 'scheduled'; sendsAt: string };

/** 'Expires 4 Oct, 7:42 PM' from the packet's expiry. */
function expiresWords(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  return `expires ${new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * The decision packet (Console SpecV5 part AA item 1): what triggered it, the real arguments,
 * how reversible it is, the price and the expiry. Details that came from something Atlas read
 * (an email, a page, a file, a ticket) are called out.
 */
function PacketBlock({ p }: { p: AtlasProposal }) {
  const k = p.packet;
  if (!k) return null;
  const meta = [k.reversibilityText, k.price, expiresWords(k.expiresAt)].filter(Boolean).join(' · ');
  return (
    <div data-testid="atlas-packet" style={sx('min-width:0;margin-top:8px;')}>
      <div style={sx('font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>
        <span style={sx('font-weight:700;')}>{k.action}.</span> Triggered by {noDash(k.origin)}.
      </div>
      {k.detailsFrom && (
        <div data-testid="atlas-packet-untrusted" style={sx('font-size:16px;font-weight:600;color:#B3261E;line-height:1.5;margin-top:3px;text-wrap:pretty;')}>
          Check the {k.untrustedFields.join(' and ')}: {noDash(k.detailsFrom)}, not from you.
        </div>
      )}
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:4px 22px;margin-top:6px;min-width:0;')}>
        {k.args.map((a) => (
          <div key={a.label} style={sx('display:flex;align-items:baseline;gap:9px;min-width:0;')}>
            <span style={sx('flex:0 0 84px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#54606C;text-transform:uppercase;')}>{a.label}</span>
            <span style={sx('flex:1 1 120px;min-width:0;font-size:16px;color:#22272E;line-height:1.5;overflow-wrap:anywhere;')}>{noDash(a.value)}</span>
          </div>
        ))}
      </div>
      {meta && <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:4px;')}>{meta}</div>}
    </div>
  );
}

/** '2h 16m waiting' or '30m 43s waiting' (the design's elapsed()). */
function waiting(iso: string, now: number): string {
  const s = Math.max(0, Math.floor((now - Date.parse(iso)) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m waiting` : `${m}m ${s % 60}s waiting`;
}

/** "Proposed by Atlas from your 19:42 question, awaiting one signature." */
function originOf(p: AtlasProposal): string {
  const left = Math.max(1, p.requiredApprovals - p.approvalsCount);
  return `${p.origin ?? 'Proposed by Atlas'}, awaiting ${inWords(left)} signature${left === 1 ? '' : 's'}.`;
}

/**
 * Actions (design: atIsActions, T13440; atlasVals atProps): anything Atlas proposed that
 * needs a person. Approving re-runs the gate on the server, which can still refuse; a
 * refusal keeps the proposal here with the reason.
 */
export function ActionsTab({ ar, proposals }: { ar: AtlasRole; proposals: QueryState<AtlasProposalsResponse> }) {
  const { client } = useSession();
  const now = useNow();
  const roReason = useReadOnlyReason();
  const [done, setDone] = useState<Record<string, Done>>({});
  const [busy, setBusy] = useState<string | null>(null);
  // partner and founder sign in as operator; the back end decides what each may sign
  const canSign = ar === 'Facility Admin' || ar === 'Operator';

  const list = (proposals.data?.proposals ?? []).filter((p) => p.status === 'pending' || done[p.id]);
  const pending = list.filter((p) => !done[p.id] || done[p.id].state === 'held').length;

  const decide = async (p: AtlasProposal, approve: boolean) => {
    if (roReason || busy) return;
    setBusy(p.id);
    try {
      const r = approve ? await atlasProposalApprove(client, { params: { id: p.id }, body: {} }) : await atlasProposalReject(client, { params: { id: p.id }, body: {} });
      const status = r.status ?? r.proposal?.status;
      // a closed proposal leaves the queue, so the tab's count ("Actions · 3") drops with it
      const closed = (st: 'approved' | 'rejected') =>
        proposals.setData((d) => (d ? { ...d, proposals: d.proposals.map((x) => (x.id === p.id ? { ...x, status: st } : x)) } : d));
      if (!approve && (status === 'rejected' || r.proposal?.status === 'rejected')) {
        setDone((d) => ({ ...d, [p.id]: { state: 'rejected' } }));
        closed('rejected');
      } else if (approve && status === 'scheduled') {
        // An email waits 60 seconds with Undo before it goes (part AA item 2).
        setDone((d) => ({ ...d, [p.id]: { state: 'scheduled', sendsAt: r.sendsAt ?? new Date(Date.now() + 60_000).toISOString() } }));
      } else if (approve && status === 'executed') {
        setDone((d) => ({ ...d, [p.id]: { state: 'approved' } }));
        closed('approved');
      }
      else {
        const left = Math.max(0, (r.proposal?.requiredApprovals ?? p.requiredApprovals) - (r.proposal?.approvalsCount ?? p.approvalsCount));
        const note = r.reason ?? r.error ?? (left > 0 ? `Signed. It needs ${inWords(left)} more signature${left === 1 ? '' : 's'} before it runs.` : 'The gate did not run it.');
        setDone((d) => ({ ...d, [p.id]: { state: 'held', note: noDash(note) } }));
      }
    } catch (e) {
      setDone((d) => ({ ...d, [p.id]: { state: 'held', note: e instanceof Error ? e.message : 'It did not go through.' } }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="atlas-actions" style={sx('flex:1 1 auto;min-height:0;min-width:0;overflow-y:auto;display:flex;flex-direction:column;')}>
      <div style={sx('flex:0 0 auto;min-width:0;padding-bottom:11px;')}>
        <div style={sx('font-size:19px;font-weight:700;letter-spacing:-.022em;color:#22272E;')}>
          {!proposals.data ? '' : pending === 0 ? 'Proposals' : pending + (pending === 1 ? ' proposal waits on a human' : ' proposals wait on a human')}
        </div>
        <div style={sx('font-size:16px;color:#3C454F;line-height:1.55;max-width:84ch;margin-top:3px;text-wrap:pretty;')}>
          {canSign
            ? 'Anything Atlas proposes that needs a human lands here instead of running from the chat. Approving re-gates it fresh and writes it to the Truth Ledger under your name.'
            : 'Anything Atlas proposes that needs a human lands here. A partner login can read the queue and cannot sign, so the approve and reject controls are simply not present.'}
        </div>
      </div>

      {proposals.loading && !proposals.data && <Skeleton rows={4} label="Loading proposals" />}
      {proposals.error && <ErrorState error={proposals.error} onRetry={proposals.reload} />}

      {proposals.data && pending === 0 && (
        <div style={sx('min-width:0;padding:22px 0;border-top:1px solid rgba(34,39,46,.14);')}>
          <div style={sx('font-size:20px;font-weight:700;letter-spacing:-.022em;color:#12503C;')}>Nothing is waiting on you.</div>
          <div style={sx('font-size:16px;color:#54606C;line-height:1.55;margin-top:5px;max-width:74ch;text-wrap:pretty;')}>
            Every proposal Atlas raised has been signed off or turned down. An empty queue is the good outcome, not a gap.
          </div>
        </div>
      )}

      {list.map((p) => {
        const d = done[p.id];
        const sending = d?.state === 'scheduled' ? d : null;
        const closed = d && d.state !== 'held' && d.state !== 'scheduled';
        const isRej = d?.state === 'rejected';
        const col = closed ? (isRej ? '#B3261E' : '#12503C') : sending ? '#12503C' : '#4A5866';
        const left = sending ? Math.max(0, Math.ceil((Date.parse(sending.sendsAt) - now) / 1000)) : 0;
        return (
          <div key={p.id} data-testid="atlas-proposal" style={sx('min-width:0;padding:14px 0;border-top:1px solid rgba(34,39,46,.14);' + (closed ? 'opacity:.6;' : ''))}>
            <div style={sx('display:flex;align-items:baseline;gap:11px;flex-wrap:wrap;row-gap:6px;min-width:0;')}>
              <span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' + col + ';background:' + col + '1A;')}>
                {closed ? (isRej ? 'TURNED DOWN' : 'APPROVED · RAN') : sending ? 'APPROVED · SENDING' : 'AWAITING A HUMAN'}
              </span>
              <span style={sx('flex:1 1 260px;min-width:0;font-size:16px;font-weight:600;letter-spacing:-.01em;line-height:1.35;color:#22272E;text-wrap:pretty;')}>{p.summary}</span>
              <span style={sx('flex:0 0 auto;font-size:14px;font-weight:600;font-variant-numeric:tabular-nums;color:' + col + ';white-space:nowrap;')}>
                {closed ? (isRej ? 'closed, nothing ran' : 'sealed to the Ledger') : sending ? (left > 0 ? `sends in ${left}s` : 'sending') : waiting(p.createdAt, now)}
              </span>
            </div>
            <div style={sx('font-size:16px;color:#54606C;line-height:1.5;margin-top:4px;text-wrap:pretty;')}>{originOf(p)}</div>
            <PacketBlock p={p} />

            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:6px 22px;margin-top:10px;min-width:0;')}>
              {(p.effects ?? []).map((f) => (
                <div key={f.k} style={sx('display:flex;align-items:baseline;gap:9px;min-width:0;')}>
                  <span style={sx('flex:0 0 84px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#54606C;')}>{f.k}</span>
                  <span style={sx('flex:1 1 120px;min-width:0;font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>{noDash(f.v)}</span>
                </div>
              ))}
            </div>

            <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:7px;margin-top:11px;min-width:0;')}>
              {sending && canSign && (
                <a
                  href="#"
                  aria-disabled={!!roReason || busy === p.id || undefined}
                  title={roReason ?? undefined}
                  onClick={(e) => {
                    stop(e);
                    void decide(p, false);
                  }}
                  style={sx('display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#B3261E;')}
                >
                  Undo
                </a>
              )}
              {!closed && !sending && canSign && (
                <>
                  <a
                    href="#"
                    aria-disabled={!!roReason || busy === p.id || undefined}
                    title={roReason ?? undefined}
                    onClick={(e) => {
                      stop(e);
                      void decide(p, true);
                    }}
                    style={sx('display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}
                  >
                    Approve and run
                  </a>
                  <a
                    href="#"
                    aria-disabled={!!roReason || busy === p.id || undefined}
                    title={roReason ?? undefined}
                    onClick={(e) => {
                      stop(e);
                      void decide(p, false);
                    }}
                    style={sx('display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#B3261E;background:rgba(179,38,30,.10);')}
                  >
                    Turn it down
                  </a>
                </>
              )}
              <span style={sx('flex:1 1 220px;min-width:0;font-size:14px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>
                {d?.state === 'held' ? d.note : sending ? 'Sending in 60 seconds. Undo stops it; nothing has gone yet.' : noDash(p.gateNote ?? (p.twoPersonRule?.applies ? p.twoPersonRule.label : ''))}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
